import { toMinor } from "./utils";
import { BASE_MODULE_ID, ORGANISATION_MODULE_ID } from "./constants";

const toLower = (value) => String(value || "").toLowerCase();

export const normalizeCompareToken = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

export const toDisplayLabel = (value, fallback = "Module") => {
  const rawValue = String(value || "")
    .replace(/-module$/i, "")
    .replace(/[-_]/g, " ")
    .trim();

  if (!rawValue) return fallback;

  return rawValue
    .split(" ")
    .filter(Boolean)
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(" ");
};

export const getTrialPlanNameFromModuleId = (moduleName) => {
  const normalizedModuleId = String(moduleName || "").toLowerCase();
  if (normalizedModuleId === BASE_MODULE_ID) return "trial";

  if (!normalizedModuleId.endsWith("-module")) return null;

  const modulePrefix = normalizedModuleId.replace(/-module$/, "");
  return modulePrefix ? `${modulePrefix}-trial` : null;
};

export const isTrialPlanName = (planName) => {
  const normalized = String(planName || "").toLowerCase();
  return normalized === "trial" || normalized.endsWith("-trial");
};

export const resolveFreeUsers = (features = {}, fallback = null) => {
  const directValue = Number(features?.users);
  if (Number.isFinite(directValue) && directValue > 0) {
    return directValue;
  }

  const fallbackValue = Number(fallback);
  if (Number.isFinite(fallbackValue) && fallbackValue > 0) {
    return fallbackValue;
  }

  return null;
};

const toLineText = (line) =>
  `${String(line?.id || "")} ${String(line?.title || "")} ${String(line?.description || "")}`.toLowerCase();

const flattenLineItems = (lineItems = []) => {
  const flat = [];

  const walk = (item) => {
    if (!item) return;
    flat.push(item);

    const children = Array.isArray(item.children) ? item.children : [];
    children.forEach(walk);
  };

  (Array.isArray(lineItems) ? lineItems : []).forEach(walk);
  return flat;
};

export const getSummaryTotalsForKeywords = (lineItems = [], keywords = []) => {
  if (!Array.isArray(keywords) || keywords.length === 0) {
    return { quantity: 0, amountMinor: 0 };
  }

  const normalizedKeywords = keywords.map((keyword) =>
    String(keyword || "").toLowerCase(),
  );

  return flattenLineItems(lineItems).reduce(
    (acc, lineItem) => {
      const haystack = toLineText(lineItem);
      const matched = normalizedKeywords.some((keyword) =>
        haystack.includes(keyword),
      );

      if (!matched) return acc;

      return {
        quantity: acc.quantity + toMinor(lineItem?.quantity),
        amountMinor: acc.amountMinor + toMinor(lineItem?.amountMinor),
      };
    },
    { quantity: 0, amountMinor: 0 },
  );
};

export const hasSharedLimitOverages = (sharedLimits = {}) =>
  Object.values(sharedLimits || {}).some(
    (entry) => Number(entry?.overLimits || 0) > 0,
  );

const toFeatureFlagLabel = (featureKey) =>
  String(featureKey || "")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase());

export const getMetadataFeatureFlags = (
  metadataFeatures = {},
  fallbackFeatures = {},
) => {
  const source =
    metadataFeatures && Object.keys(metadataFeatures).length
      ? metadataFeatures
      : fallbackFeatures;

  return Object.entries(source)
    .filter(([, value]) => typeof value === "boolean")
    .map(([key, enabled]) => ({
      id: `metadata-${key}`,
      label: toFeatureFlagLabel(key),
      enabled,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const toSummaryLineAmountMinor = (lineItem) => {
  if (typeof lineItem?.amountMinor === "number") {
    return toMinor(lineItem.amountMinor);
  }

  if (typeof lineItem?.amount === "number") {
    return toMinor(lineItem.amount * 100);
  }

  return 0;
};

const getFallbackCadenceKey = (summary = {}) => {
  const billedText = String(
    summary?.billed || summary?.commitmentTerm || "",
  ).toLowerCase();
  return billedText.includes("annual") ? "annual" : "monthly";
};

export const getLineItemCadenceKey = (
  lineItemId,
  moduleSubscriptions,
  summary,
) => {
  if (String(lineItemId || "") === ORGANISATION_MODULE_ID) {
    return "monthly";
  }

  const matchedModule = (moduleSubscriptions || []).find(
    (module) => String(module?.id || "") === String(lineItemId || ""),
  );

  if (matchedModule?.period === "year") return "annual";
  if (matchedModule?.period === "month") return "monthly";

  return getFallbackCadenceKey(summary);
};

export const getAnnualizedTotalMinorFromSummary = ({
  summary,
  moduleSubscriptions,
}) => {
  const lineItems = Array.isArray(summary?.lineItems) ? summary.lineItems : [];

  if (!lineItems.length) {
    return null;
  }

  const totalsByCadence = lineItems.reduce(
    (acc, lineItem) => {
      const cadenceKey = getLineItemCadenceKey(
        lineItem?.id,
        moduleSubscriptions,
        summary,
      );
      const lineAmountMinor = toSummaryLineAmountMinor(lineItem);
      const childAmountMinor = (
        Array.isArray(lineItem?.children) ? lineItem.children : []
      ).reduce((childTotal, child) => {
        return childTotal + toSummaryLineAmountMinor(child);
      }, 0);

      acc[cadenceKey] += lineAmountMinor + childAmountMinor;
      return acc;
    },
    { monthly: 0, annual: 0 },
  );

  return totalsByCadence.annual + totalsByCadence.monthly * 12;
};

export const isExpiredTrialModule = (module, now = new Date()) => {
  if (!module?.trialEndsAt) return false;

  const trialEndDate = new Date(module.trialEndsAt);
  if (Number.isNaN(trialEndDate.getTime())) return false;

  return trialEndDate < now && !module?.isActive;
};

// Organisation add-ons have their own rail section, so never default to them.
export const getDefaultModuleId = (
  activeModules = [],
  availableModules = [],
) => {
  const isSelectableModule = (module) => module?.id !== ORGANISATION_MODULE_ID;

  return (
    (
      activeModules.find(isSelectableModule) ||
      availableModules.find(isSelectableModule)
    )?.id || null
  );
};

const sumMatchingLineItems = (lineItems, predicate, projector) => {
  return lineItems.reduce((total, lineItem) => {
    const baseTotal = predicate(lineItem)
      ? total + toMinor(projector(lineItem))
      : total;

    const children = Array.isArray(lineItem?.children) ? lineItem.children : [];
    const childrenTotal = children.reduce((childTotal, child) => {
      if (!predicate(child)) return childTotal;
      return childTotal + toMinor(projector(child));
    }, 0);

    return baseTotal + childrenTotal;
  }, 0);
};

export const isAdditionalUsersLineItem = (line) => {
  const lineId = toLower(line?.id);
  const lineTitle = toLower(line?.title);

  return (
    lineId.includes("additional-users") ||
    lineTitle.includes("additional users")
  );
};

export const sumAdditionalUsersQuantityFromSummary = (lineItems = []) =>
  sumMatchingLineItems(
    Array.isArray(lineItems) ? lineItems : [],
    isAdditionalUsersLineItem,
    (line) => line?.quantity,
  );

export const sumAdditionalUsersAmountMinorFromSummary = (lineItems = []) =>
  sumMatchingLineItems(
    Array.isArray(lineItems) ? lineItems : [],
    isAdditionalUsersLineItem,
    (line) => line?.amountMinor,
  );

// Fetching organisation add-ons creates the module server-side, so modules loaded before it are stale.
export const isOrganisationModuleMissingAfterAddonsLoad = ({
  organisationAvailableAddons,
  moduleSubscriptions = [],
  loadingSubscription,
}) =>
  !loadingSubscription &&
  Array.isArray(organisationAvailableAddons) &&
  !moduleSubscriptions.some((module) => module?.id === ORGANISATION_MODULE_ID);
